import type { BulkPermissionChangesJSON } from "permissions/_api/permissions-api-service";
import Permission from "permissions/permission";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { LegalEntityAccessType } from "permissions/resources/legal-entities/legal-entity-access-types";
import type { Resource } from "permissions/resources/permission-resource";
import {
  type GranteeTemplateChanges,
  type TemplateResourceChange,
  TemplateChangeKind,
  resourceSignature,
} from "permissions/templates/template-application-plan";

/**
 * What applying a template did to one person, from the server's answer, set against the preview.
 *
 * The preview is worked out on the client from the grants it read; the answer is what the server
 * did. They should agree, and where they do not — a grant somebody else made in between, a raise the
 * preview read as a grant, the derived grants withdrawn behind a replaced one, which the preview
 * cannot know — the summary says so rather than leaving the reader to compare two lists.
 */

export type SummaryRow = Readonly<{
  permission: Permission;
  /** Whether the preview said this would happen. Withdrawn grants never are: it cannot know them. */
  expected: boolean;
}>;

export type GranteeApplicationSummary = Readonly<{
  granted: readonly SummaryRow[];
  raised: readonly SummaryRow[];
  removed: readonly SummaryRow[];
  withdrawn: readonly SummaryRow[];
  /** What the preview said would happen and the server did not do. */
  missing: readonly TemplateResourceChange[];
}>;

function rows(json: BulkPermissionChangesJSON["granted"], isExpected: (permission: Permission) => boolean): SummaryRow[] {
  return Permission.fromJSONArray(json).map((permission) => ({ permission, expected: isExpected(permission) }));
}

export function summarizeApplication(
  preview: GranteeTemplateChanges | undefined,
  answer: BulkPermissionChangesJSON,
): GranteeApplicationSummary {
  const changes = preview?.changes ?? [];
  const toGrant = new Set(
    changes.filter((change) => change.kind === TemplateChangeKind.Granted).map((change) => resourceSignature(change.resource)),
  );
  const toRevoke = new Set(
    changes
      .filter((change) => change.kind === TemplateChangeKind.Revoked)
      .map((change) => change.existingPermission?.id?.value)
      .filter((id): id is string => Boolean(id)),
  );

  const granted = rows(answer.granted, (permission) => toGrant.has(resourceSignature(permission.resource)));
  const raised = rows(answer.raised, (permission) => toGrant.has(resourceSignature(permission.resource)));
  const removed = rows(answer.disabled, (permission) => Boolean(permission.id && toRevoke.has(permission.id.value)));
  const withdrawn = rows(answer.withdrawn, () => false);

  const done = new Set([...granted, ...raised].map((row) => resourceSignature(row.permission.resource)));
  const removedIds = new Set(removed.map((row) => row.permission.id?.value));
  const missing = changes.filter(
    (change) =>
      (change.kind === TemplateChangeKind.Granted && !done.has(resourceSignature(change.resource))) ||
      (change.kind === TemplateChangeKind.Revoked && !removedIds.has(change.existingPermission?.id?.value)),
  );

  return { granted, raised, removed, withdrawn, missing };
}

function isSensitiveKind(resource: Resource): boolean {
  return (
    resource instanceof LegalEntityAccess &&
    (resource.accessType === LegalEntityAccessType["Signing Authority"] ||
      resource.accessType === LegalEntityAccessType["Member Permissions"])
  );
}

/**
 * Whether the template names signing authority or Member Permissions, and so can take either away.
 * Asked when the preview cannot say whether it does: a failed or cut-short read is no reason to skip
 * the second word.
 */
export function namesSensitiveKind(resources: readonly Resource[]): boolean {
  return resources.some(isSensitiveKind);
}

/**
 * The removals that need a second word before applying: taking away signing authority, or the Member
 * Permissions that let somebody run this page. Either can leave an organization unable to sign or to
 * put the grant back.
 */
export function sensitiveRemovals(preview: readonly GranteeTemplateChanges[]): Array<Readonly<{ granteeUserId: GranteeTemplateChanges["granteeUserId"]; change: TemplateResourceChange }>> {
  return preview.flatMap((grantee) =>
    grantee.changes
      .filter(
        (change) =>
          change.kind === TemplateChangeKind.Revoked && isSensitiveKind(change.resource),
      )
      .map((change) => ({ granteeUserId: grantee.granteeUserId, change })),
  );
}
